import { memo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import AmountContainer from "app/pages/.shared/AmountContainer";
import { FormattedMessage } from "react-intl";
import IconArrowDown from "app/pages/.shared/static/icons/IconArrowDown";
import Modal from "react-modal";
import IconCircleClose from "app/pages/.shared/IconCircleClose";
import HolidaySummaryContainer from "app/pages/Booking/HolidaySummary/HolidaySummary/HolidaySummaryContainer";
import "./CollapsableHolidaySummary.scss";
import Collapse from "app/components/ui/collapse/Collapse";
import { QuotationPriceDetailContainer } from "app/pages/Booking/Quotation/PriceDetails/QuotationPriceDetailContainer";
import useBlockScroll from "app/utils/hooks/useBlockScroll";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { calculateDiscountPercentage } from "app/utils/utils";

const style = {
	overlay: {
		backgroundColor: "rgba(43, 56, 63, .7)",
		zIndex: 5,
	},
};

const CollapsableHolidaySummary = ({ totalPrice, savingsTotalAmount = 0 }) => {
	const [isOpen, toggle] = useState(false);

	const handleClick = useCallback(() => {
		toggle(!isOpen);
	}, [isOpen]);

	useBlockScroll(isOpen);

	const publicPrice = savingsTotalAmount > 0 ? totalPrice + savingsTotalAmount : 0;

	const discountPercentage = calculateDiscountPercentage({
		publicPrice,
		savingsTotalAmount: savingsTotalAmount,
	});

	return (
		<>
			<div className="collapsable-holiday-summary" onClick={handleClick}>
				<div className="collapsable-holiday-summary__title">
					{isOpen ? (
						<>
							<IconCircleClose
								height={25}
								width={25}
								style={{ marginRight: "10px" }}
							/>
							<FormattedMessage id="booking.holiday.summary.collapsable.title" />
						</>
					) : (
						<>
							<IconArrowDown height={20} width={15} style={{ marginRight: "10px" }} />
							<FormattedMessage id="booking.holiday.summary.collapsable.title" />
						</>
					)}
				</div>
				{totalPrice > 0 && (
					<div className="collapsable-holiday-summary__total">
						<div className="collapsable-holiday-summary__total-discount-percentage">
							{discountPercentage > 0 && (
								<Typography
									variant={TYPOGRAPHY_VARIANTS.SMALL}
									component="span"
									className="collapsable-holiday-summary__discount-percentage"
								>
									-{discountPercentage}%
								</Typography>
							)}
							<AmountContainer amount={totalPrice} />
						</div>
						{publicPrice > 0 && (
							<Typography
								variant={TYPOGRAPHY_VARIANTS.SMALL}
								component="div"
								className="collapsable-holiday-summary__reference"
							>
								<FormattedMessage
									id="general.instead.of.price.with.amount"
									values={{
										price: (
											<AmountContainer
												className="collapsable-holiday-summary__amount"
												amount={publicPrice}
											/>
										),
									}}
								/>
							</Typography>
						)}
					</div>
				)}
			</div>
			<Modal
				style={style}
				isOpen={isOpen}
				parentSelector={() => document.querySelector(".booking")}
				onRequestClose={handleClick}
				className="collapsable-holiday-summary__modal"
				overlayClassName="collapsable-holiday-summary__overlay"
			>
				<div className="collapsable-holiday-summary__content">
					<HolidaySummaryContainer showBookingBanner />

					<Collapse
						className="collapsable-holiday-summary__collapse"
						expandIcon={({ isActive }) => {
							return (
								<IconArrowDown
									style={{
										transform: `rotate(${isActive ? 180 : 0}deg)`,
									}}
								/>
							);
						}}
					>
						<Collapse.Panel
							headerClass="collapsable-holiday-summary__collapse-header"
							header={
								<div className="collapsable-holiday-summary__collapse-title">
									<FormattedMessage id="booking.holiday.summary.collapsable.price.breakdown" />
								</div>
							}
						>
							<QuotationPriceDetailContainer />
						</Collapse.Panel>
					</Collapse>
				</div>
			</Modal>
		</>
	);
};

CollapsableHolidaySummary.propTypes = {
	totalPrice: PropTypes.number,
	savingsTotalAmount: PropTypes.number,
};

export default memo(CollapsableHolidaySummary);
